import React, { useEffect, useRef, useState } from 'react';
import {
  Chart as ChartJS,
  ArcElement,
  Tooltip,
  Legend,
  DoughnutController,
  CategoryScale,
  LinearScale
} from 'chart.js';

ChartJS.register(
  ArcElement,
  Tooltip,
  Legend,
  DoughnutController,
  CategoryScale,
  LinearScale
);
import { useLanguage } from '../contexts/LanguageContext';
import { useAuth } from '../contexts/AuthContext';
import { dataCache } from '../utils/cache';
import { api, fetchWithAuth } from '../config/api';
import SearchableDropdown from './SearchableDropdown';
import './Sidebar.css';

const Sidebar = ({ 
  statistics, 
  zipcodeFilters, 
  onZipcodeFiltersChange, 
  onApplyZipcodeFilters, 
  dmas = [], 
  estados = [], 
  regions = [], 
  certainteedStates = [], 
  certainteedDbas = [],
  apolloDbas = [],
  certainteedBranches = [], 
  filteredBranches = [],
  qxoBranches = [],
  qxoCities = [],
  certainteedCities = [],
  apolloCities = [],
  layerVisibility,
  onLayerVisibilityChange,
  hispanicConcentration,
  setHispanicConcentration,
  clearCache,
  tradeAreaLoading = false,
  onShowTopHispanic,
  onClearTopHispanic,
  isTopHispanicActive = false,
  contractorFilters,
  setContractorFilters
}) => {
  const { t, getTranslations } = useLanguage();
  const { logout } = useAuth();
  const chartRef = useRef(null);
  const chartInstance = useRef(null);
  const [cacheStats, setCacheStats] = useState(null);
  const [totalStats, setTotalStats] = useState(null);
  const loadingTotalStatsRef = useRef(false);
  const [showContractorFilter, setShowContractorFilter] = useState(false); // Estado para mostrar el popup de filtros

  // Debug: ver qué datos llegan
      
  // Cargar estadísticas totales cuando cambien los filtros
  useEffect(() => {
    const loadTotalStats = async () => {
      // Guard para evitar llamadas duplicadas
      if (loadingTotalStatsRef.current) {
                return;
      }
      
      loadingTotalStatsRef.current = true;
      try {
        let url = api.statisticsTopHispanic();
        const params = new URLSearchParams();
        
        if (zipcodeFilters.dma) params.append('dma', zipcodeFilters.dma);
        if (zipcodeFilters.state) params.append('estado', zipcodeFilters.state);
        if (zipcodeFilters.region) params.append('region', zipcodeFilters.region);
        if (zipcodeFilters.certainteedBranch) {
          params.append('branch', zipcodeFilters.certainteedBranch);
        }

        if (params.toString()) {
          url += `?${params.toString()}`;
        }

                
        const response = await fetchWithAuth(url);
        if (response.ok) {
          const data = await response.json();
          setTotalStats(data);
        }
      } catch (error) {
              } finally {
        loadingTotalStatsRef.current = false;
      }
    };
    
    loadTotalStats();
  }, [zipcodeFilters.dma, zipcodeFilters.state, zipcodeFilters.region, zipcodeFilters.certainteedBranch]);

  useEffect(() => {
    if (statistics && Object.keys(statistics).length > 0) {
      createChart();
    }
    return () => {
      if (chartInstance.current) {
        chartInstance.current.destroy();
        chartInstance.current = null;
      }
    };
  }, [statistics]);

  // Actualizar estadísticas del cache cada 10 segundos
  useEffect(() => {
    const updateCacheStats = () => {
      const stats = dataCache.getStats();
      setCacheStats(stats);
    };

    updateCacheStats();
    const interval = setInterval(updateCacheStats, 10000);

    return () => clearInterval(interval);
  }, []);

  const createChart = () => {
    try {
      if (!chartRef.current) {
                return;
      }

      if (chartInstance.current) {
        chartInstance.current.destroy();
      }

      const ctx = chartRef.current.getContext('2d');
      if (!ctx) {
                return;
      }

      const countries = getTranslations('countries');
      if (!countries) {
                return;
      }
      
      const chartData = {
        labels: [
          countries.mexicano,
          countries.puerto_rico,
          countries.cubano,
          countries.colombia,
          countries.dom_republic,
          countries.guatemala,
          countries.el_salvador,
          countries.honduras,
          countries.ecuador,
          countries.peru,
          countries.venezuela,
          countries.espana
        ],
        datasets: [{
          data: [
            statistics.total_mexicano || 0,
            statistics.total_puerto_rico || 0,
            statistics.total_cubano || 0,
            statistics.total_colombia || 0,
            statistics.total_dom_republic || 0,
            statistics.total_guatemala || 0,
            statistics.total_el_salvador || 0,
            statistics.total_honduras || 0,
            statistics.total_ecuador || 0,
            statistics.total_peru || 0,
            statistics.total_venezuela || 0,
            statistics.total_espana || 0
          ],
          backgroundColor: [
            '#FF6384', '#36A2EB', '#FFCE56', '#4BC0C0', '#9966FF', '#FF9F40',
            '#FF6384', '#36A2EB', '#FFCE56', '#4BC0C0', '#9966FF', '#FF9F40'
          ],
          borderWidth: 2,
          borderColor: '#fff'
        }]
      };

      chartInstance.current = new ChartJS(ctx, {
        type: 'doughnut',
        data: chartData,
        options: {
          responsive: true,
          maintainAspectRatio: false,
          plugins: {
            legend: {
              position: 'bottom',
              labels: {
                font: {
                  size: 10
                }
              }
            }
          }
        }
      });
    } catch (error) {
          }
  };

  const formatNumber = (num) => {
    if (!num) return '0';
    return new Intl.NumberFormat('en-US').format(num);
  };

  const formatBytes = (bytes) => {
    if (bytes === 0) return '0 B';
    const k = 1024;
    const sizes = ['B', 'KB', 'MB', 'GB'];
    const i = Math.floor(Math.log(bytes) / Math.log(k));
    return parseFloat((bytes / Math.pow(k, i)).toFixed(1)) + ' ' + sizes[i];
  };

  const handleFilterChange = (key, value) => {
    // Si cambia el estado, limpiar el filtro de sucursal
    if (key === 'state') {
      onZipcodeFiltersChange({
        ...zipcodeFilters,
        [key]: value,
        certainteedBranch: ''
      });
    } else {
      onZipcodeFiltersChange({
        ...zipcodeFilters,
        [key]: value
      });
    }
  };

  const handleLayerToggle = (layer) => {
    onLayerVisibilityChange(layer, !layerVisibility[layer]);
  };

  const handleClearCache = () => {
    if (window.confirm(t('cache.clearConfirm'))) {
      onClearCache();
      setCacheStats(dataCache.getStats());
    }
  };

  function abbreviateNumber(num) {
    if (num >= 1e9) return (num / 1e9).toFixed(1).replace(/\.0$/, '') + 'B';
    if (num >= 1e6) return (num / 1e6).toFixed(1).replace(/\.0$/, '') + 'M';
    if (num >= 1e3) return (num / 1e3).toFixed(1).replace(/\.0$/, '') + 'K';
    return num;
  }

  return (
    <div className={`sidebar ${tradeAreaLoading ? 'has-overlay' : ''}`}>
      {/* Overlay de bloqueo cuando está cargando */}
      {tradeAreaLoading && (
        <div className="sidebar-overlay">
          <div className="overlay-content">
            <div className="overlay-spinner">
              <i className="fas fa-spinner fa-spin"></i>
            </div>
            <div className="overlay-text">
              Loading trade areas...
            </div>
          </div>
        </div>
      )}
      
      <div className="sidebar-header">
        <div className="header">
          <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', width: '100%' }}>
            <div>
              <h4><i className="fas fa-map-marked-alt"></i> {t('header.title')}</h4>
              <p className="mb-0">{t('header.subtitle')}</p>
            </div>
            <button 
              onClick={logout}
              style={{
                background: '#dc3545',
                color: 'white',
                border: 'none',
                padding: '8px 16px',
                borderRadius: '6px',
                cursor: 'pointer',
                fontSize: '14px',
                fontWeight: '500',
                display: 'flex',
                alignItems: 'center',
                gap: '6px',
                transition: 'all 0.3s ease'
              }}
              onMouseOver={(e) => e.target.style.background = '#c82333'}
              onMouseOut={(e) => e.target.style.background = '#dc3545'}
              title="Logout"
            >
              <i className="fas fa-sign-out-alt"></i>
              Logout
            </button>
          </div>
        </div>
      </div>
      

      {/* Chat Analytics Link */}
      <div style={{
        padding: '12px 15px',
        borderBottom: '1px solid #dee2e6',
        background: 'linear-gradient(135deg, #1a1a2e 0%, #16213e 100%)'
      }}>
        <a
          href="/chat"
          style={{
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            gap: '8px',
            background: '#0ea5e9',
            color: 'white',
            border: 'none',
            padding: '10px 16px',
            borderRadius: '8px',
            cursor: 'pointer',
            fontSize: '14px',
            fontWeight: '600',
            textDecoration: 'none',
            transition: 'all 0.3s ease',
            width: '100%'
          }}
          onMouseOver={(e) => e.currentTarget.style.background = '#0284c7'}
          onMouseOut={(e) => e.currentTarget.style.background = '#0ea5e9'}
        >
          <i className="fas fa-comments"></i>
          Chat Analytics
          <i className="fas fa-arrow-right" style={{ fontSize: '12px', marginLeft: 'auto' }}></i>
        </a>
      </div>

      <div className="filters-header">
        <h5><i className="fas fa-filter"></i> {t('filters.title')}</h5>
      </div>

     
      {/* Control de capas */}
      <div className="layers-section">
        <h6><i className="fas fa-layer-group"></i> {t('layers.title')}</h6>
        
        <div className="layer-toggle">
          <label className="toggle-switch">
            <input
              type="checkbox"
              checked={layerVisibility.densityZip}
              onChange={() => handleLayerToggle('densityZip')}
              disabled={tradeAreaLoading}
            />
            <span className="toggle-slider"></span>
          </label>
          <span className="toggle-label">
            🟨 {t('layers.densityZip')} {layerVisibility.densityZip ? t('layers.visible') : t('layers.hidden')}
          </span>
        </div>
        
        <div className="layer-toggle">
          <label className="toggle-switch">
            <input
              type="checkbox"
              checked={layerVisibility.certainteedTradeArea}
              onChange={() => onLayerVisibilityChange('certainteedTradeArea', !layerVisibility.certainteedTradeArea)}
              disabled={tradeAreaLoading}
            />
            <span className="toggle-slider"></span>
          </label>
          <span className="toggle-label">
            🟦 Show SRS Trade Area (polygons)
          </span>
        </div>
        
        <div className="layer-toggle">
          <label className="toggle-switch">
            <input
              type="checkbox"
              checked={layerVisibility.certainteed}
              onChange={() => handleLayerToggle('certainteed')}
              disabled={tradeAreaLoading}
            />
            <span className="toggle-slider"></span>
          </label>
          <span className="toggle-label">
            📍 {t('layers.certainteed')}
          </span>
        </div>
        
        <div className="layer-toggle">
          <label className="toggle-switch">
            <input
              type="checkbox"
              checked={layerVisibility.apollo}
              onChange={() => handleLayerToggle('apollo')}
              disabled={tradeAreaLoading}
            />
            <span className="toggle-slider"></span>
          </label>
          <span className="toggle-label">
            🟦 CertainTeed Current Certified Contractors
          </span>
          {layerVisibility.apollo && (
            <button 
              className="filter-btn-small"
              onClick={() => setShowContractorFilter(!showContractorFilter)}
              title="Filter Contractor Types"
              style={{
                marginLeft: 'auto',
                background: 'none',
                border: '1px solid #ccc',
                borderRadius: '4px',
                cursor: 'pointer',
                padding: '2px 6px',
                fontSize: '12px',
                color: '#555'
              }}
            >
              <i className="fas fa-filter"></i>
            </button>
          )}
        </div>

        {/* Popup de filtros de contratistas */}
        {showContractorFilter && layerVisibility.apollo && (
          <div className="contractor-filter-popup" style={{
            background: 'var(--panel)',
            border: '1px solid var(--line)',
            borderRadius: '6px',
            padding: '10px',
            marginTop: '-5px',
            marginBottom: '10px',
            boxShadow: '0 4px 12px var(--shadow-medium)',
            fontSize: '13px',
            color: 'var(--ink)'
          }}>
            <div style={{display: 'flex', justifyContent: 'space-between', marginBottom: '8px', borderBottom: '1px solid var(--line)', paddingBottom: '5px'}}>
              <strong>Filter by Type</strong>
              <span
                onClick={() => setShowContractorFilter(false)}
                style={{cursor: 'pointer', color: 'var(--muted)'}}
              >
                <i className="fas fa-times"></i>
              </span>
            </div>
            
            <div className="contractor-types-list">
              {[
                { id: 'azul-oscuro', label: 'SSM Certified', color: '#003d82' },
                { id: 'verde', label: 'SM Certified', color: '#28a745' },
                { id: 'naranja-oscuro', label: 'STAR Certified', color: '#ff8c00' },
                { id: 'gris', label: 'SMARTCOAT Certified', color: '#6c757d' },
                { id: 'rosa-fucsia', label: '5 Star Company', color: '#e91e63' },
                { id: 'rojo', label: 'Silver Star Siding', color: '#dc3545' }
              ].map(type => (
                <div key={type.id} style={{display: 'flex', alignItems: 'center', marginBottom: '5px'}}>
                  <input
                    type="checkbox"
                    id={`filter-${type.id}`}
                    checked={contractorFilters[type.id]}
                    onChange={(e) => {
                      setContractorFilters({
                        ...contractorFilters,
                        [type.id]: e.target.checked
                      });
                    }}
                    style={{marginRight: '8px', accentColor: 'var(--green)'}}
                  />
                  <span
                    style={{
                      display: 'inline-block',
                      width: '10px',
                      height: '10px',
                      borderRadius: '50%',
                      backgroundColor: type.color,
                      // aro tenue: sobre el panel oscuro el azul de SSM (#003d82)
                      // queda casi invisible sin un borde que lo despegue del fondo
                      boxShadow: '0 0 0 1px rgba(255,255,255,0.28)',
                      marginRight: '6px'
                    }}
                  ></span>
                  <label htmlFor={`filter-${type.id}`} style={{cursor: 'pointer', margin: 0}}>
                    {type.label}
                  </label>
                </div>
              ))}
            </div>
            
            <div style={{marginTop: '8px', paddingTop: '5px', borderTop: '1px solid var(--line)', display: 'flex', justifyContent: 'space-between'}}>
              <button 
                onClick={() => {
                  const allTrue = {};
                  Object.keys(contractorFilters).forEach(k => allTrue[k] = true);
                  setContractorFilters(allTrue);
                }}
                style={{fontSize: '11px', border: 'none', background: 'none', color: 'var(--green)', cursor: 'pointer', padding: 0}}
              >
                Select All
              </button>
              <button 
                onClick={() => {
                  const allFalse = {};
                  Object.keys(contractorFilters).forEach(k => allFalse[k] = false);
                  setContractorFilters(allFalse);
                }}
                style={{fontSize: '11px', border: 'none', background: 'none', color: 'var(--muted)', cursor: 'pointer', padding: 0}}
              >
                Clear All
              </button>
            </div>
          </div>
        )}
        
        <div className="layer-toggle">
          <label className="toggle-switch">
            <input
              type="checkbox"
              checked={layerVisibility.notCertifiedContractors}
              onChange={() => handleLayerToggle('notCertifiedContractors')}
              disabled={tradeAreaLoading}
            />
            <span className="toggle-slider"></span>
          </label>
          <span className="toggle-label">
            🟣 Not Certified Contractors
          </span>
        </div>
        
        <div className="layer-toggle">
          <label className="toggle-switch">
            <input
              type="checkbox"
              checked={layerVisibility.qxoTradeArea}
              onChange={() => handleLayerToggle('qxoTradeArea')}
              disabled={tradeAreaLoading}
            />
            <span className="toggle-slider"></span>
          </label>
          <span className="toggle-label">
            🟩 Show QXO Trade Area (polygons)
          </span>
        </div>
        
        <div className="layer-toggle">
          <label className="toggle-switch">
            <input
              type="checkbox"
              checked={layerVisibility.qxo}
              onChange={() => handleLayerToggle('qxo')}
              disabled={tradeAreaLoading}
            />
            <span className="toggle-slider"></span>
          </label>
          <span className="toggle-label">
            🟢 QXO Branches
          </span>
        </div>
        
        <div className="layers-description">
          {t('layers.description')}
        </div>
      </div>
      
      <div className="controls">
        <div className="filter-group" style={{display: 'none'}}>
          <label className="filter-label">{t('filters.country')}</label>
          <select 
            value={zipcodeFilters.country} 
            onChange={(e) => handleFilterChange('country', e.target.value)}
            className="form-select"
            disabled={tradeAreaLoading}
          >
            <option value="">{t('filters.allCountries')}</option>
            <option value="mexicano">{t('countries.mexicano')}</option>
            <option value="puerto_rico">{t('countries.puerto_rico')}</option>
            <option value="cubano">{t('countries.cubano')}</option>
            <option value="colombia">{t('countries.colombia')}</option>
            <option value="dom_republic">{t('countries.dom_republic')}</option>
            <option value="guatemala">{t('countries.guatemala')}</option>
            <option value="el_salvador">{t('countries.el_salvador')}</option>
            <option value="honduras">{t('countries.honduras')}</option>
            <option value="ecuador">{t('countries.ecuador')}</option>
            <option value="peru">{t('countries.peru')}</option>
            <option value="venezuela">{t('countries.venezuela')}</option>
            <option value="espana">{t('countries.espana')}</option>
          </select>
        </div>
        
        <div className="filter-group" style={{display: 'none'}}>
          <label className="filter-label">{t('filters.dma')}</label>
          <select 
            value={zipcodeFilters.dma} 
            onChange={(e) => handleFilterChange('dma', e.target.value)}
            className="form-select"
            disabled={tradeAreaLoading}
          >
            <option value="">{t('filters.allDmas')}</option>
            {dmas.map(dma => (
              <option key={dma.dma_code} value={dma.dma_code}>
                {dma.dma_name || `DMA ${dma.dma_code}`}
              </option>
            ))}
          </select>
        </div>
        
        <div className="filter-group">
          <label className="filter-label">{t('filters.estado')}</label>
          <select 
            value={zipcodeFilters.state} 
            onChange={(e) => handleFilterChange('state', e.target.value)}
            className="form-select"
            disabled={tradeAreaLoading}
          >
            <option value="">{t('filters.allEstados')}</option>
            {estados.map(estado => (
              <option key={estado.value} value={estado.value}>{estado.label}</option>
            ))}
          </select>
        </div>
        
        <div className="filter-group">
          <label className="filter-label">🏙️ City</label>
          <SearchableDropdown
            id="city-filter"
            multiple
            itemNoun="cities"
            options={(() => {
              // Si solo Apollo está visible, mostrar solo ciudades de Apollo
              const onlyApolloVisible = layerVisibility.apollo && 
                                       !layerVisibility.certainteed && 
                                       !layerVisibility.qxo;
              
              if (onlyApolloVisible) {
                // Solo mostrar ciudades de Apollo
                if (apolloCities && apolloCities.length > 0) {
                  return [...apolloCities].sort();
                }
                return [];
              }
              
              // Combinar ciudades de QXO, SRS/Certainteed y Apollo, eliminando duplicados
              const allCities = new Set();
              if (layerVisibility.qxo && qxoCities && qxoCities.length > 0) {
                qxoCities.forEach(city => allCities.add(city));
              }
              if (layerVisibility.certainteed && certainteedCities && certainteedCities.length > 0) {
                certainteedCities.forEach(city => allCities.add(city));
              }
              if (layerVisibility.apollo && apolloCities && apolloCities.length > 0) {
                apolloCities.forEach(city => allCities.add(city));
              }
              return Array.from(allCities).sort();
            })()}
            value={Array.isArray(zipcodeFilters.city) ? zipcodeFilters.city : []}
            onChange={(cities) => handleFilterChange('city', cities)}
            placeholder="Search city..."
            emptyLabel="All cities"
          />
        </div>

        <div className="filter-group">
          <label className="filter-label">Certified Contractors</label>
          <SearchableDropdown
            id="dba-filter"
            options={apolloDbas || []}
            value={zipcodeFilters.dba || ''}
            onChange={(value) => handleFilterChange('dba', value)}
            placeholder="Search company..."
            emptyLabel="All Companies"
          />
        </div>
        
        {false && (
          <div className="filter-group">
            <label className="filter-label">🏢 {t('filters.sucursal')}</label>
            <select 
              value={zipcodeFilters.certainteedBranch || ''} 
              onChange={(e) => handleFilterChange('certainteedBranch', e.target.value)}
              className="form-select"
              disabled={tradeAreaLoading}
            >
              <option value="">{t('filters.allSucursales')}</option>
              {filteredBranches && filteredBranches.map(branch => (
                <option key={branch} value={branch}>{branch}</option>
              ))}
            </select>
          </div>
        )}

        
        <div className="filter-group" style={{display: 'none'}}>
          <label className="filter-label">{t('filters.region')}</label>
          <select 
            value={zipcodeFilters.region} 
            onChange={(e) => handleFilterChange('region', e.target.value)}
            className="form-select"
            disabled={tradeAreaLoading}
          >
            <option value="">{t('filters.allRegions')}</option>
            {regions.map(region => (
              <option key={region} value={region}>{region}</option>
            ))}
          </select>
        </div>

        
        
        <div className="ranking-buttons">
          <button 
            className={`ranking-btn ${isTopHispanicActive ? 'active' : ''}`}
            onClick={onShowTopHispanic}
            disabled={tradeAreaLoading || !layerVisibility.certainteedTradeArea}
            title={!layerVisibility.certainteedTradeArea ? 'Enable "Show CertainTeed Trade Area (polygons)" first' : ''}
          >
            <i className="fas fa-trophy"></i> {t('filters.showTopHispanic')}
          </button>
          
          {isTopHispanicActive && (
            <button 
              className="clear-ranking-btn"
              onClick={onClearTopHispanic}
              disabled={tradeAreaLoading}
            >
              <i className="fas fa-times"></i> {t('filters.clearTopHispanic')}
            </button>
          )}

          {/* Download Top 200 CSV button - Oculto */}
          {/* <a
            href={api.exportTop200()}
            className="ranking-btn"
            style={{ textDecoration: 'none', display: 'inline-flex', alignItems: 'center', gap: '6px' }}
            download
            title="Download Top 200 SRS (CSV)"
          >
            <i className="fas fa-file-download"></i> Download Top 200 (CSV)
          </a> */}
        </div>
        
        {/* Indicador de loading para trade areas */}
        {tradeAreaLoading && (
          <div className="trade-area-loading">
            <div className="loading-spinner">
              <i className="fas fa-spinner fa-spin"></i>
            </div>
            <div className="loading-text">
              Loading trade areas...
            </div>
          </div>
        )}
      </div>

      {/* Estadísticas */}
      <div className="statistics-section">
        <h6><i className="fas fa-chart-pie"></i>  SRS Branches Coverage</h6>
        <div className="stats-grid">
          <div className="stat-card">
            <div className="stat-number">{formatNumber(statistics.total_zipcodes)}</div>
            <div className="stat-label">{t('statistics.zipcodes')}</div>
            {totalStats && (
              <div className="stat-total">
                <small>Total: {formatNumber(totalStats.total_zipcodes)}</small>
              </div>
            )}
          </div>
          <div className="stat-card">
            <div className="stat-number">{formatNumber(statistics.total_population)}</div>
            <div className="stat-label">{t('statistics.totalPopulation')}</div>
            {totalStats && (
              <div className="stat-total">
                <small>Total: {formatNumber(totalStats.total_population)}</small>
              </div>
            )}
          </div>
          <div className="stat-card">
            <div className="stat-number">{formatNumber(statistics.total_hispanic)}</div>
            <div className="stat-label">{t('statistics.hispanicPopulation')}</div>
            {totalStats && (
              <div className="stat-total">
                <small>Total: {formatNumber(totalStats.total_hispanic)}</small>
              </div>
            )}
          </div>
          <div className="stat-card">
            <div className="stat-number">{statistics.avg_hispanic_percentage}%</div>
            <div className="stat-label">{t('statistics.averagePercentage')}</div>
            {totalStats && (
              <div className="stat-total">
                <small>
                  {(() => {
                    // Numerador: hispanos en cobertura (sólo sucursales) del contexto actual
                    const th = Number(statistics?.total_hispanic) || 0;
                    // Denominador: población total del universo (totales) del mismo contexto
                    const tp = Number(totalStats?.total_population) || 0;
                    const pct = tp > 0 ? ((th / tp) * 100).toFixed(2) : '0.00';
                    return `Total: ${pct}%`;
                  })()}
                </small>
              </div>
            )}
          </div>
        </div>
      </div>

      
      {/* Barras: Store Count by Hispanic Concentration (NUEVO AVANZADO) */}
      {statistics.store_count_by_concentration && (
        <div className="bar-section">
          <h6>
            {t('statistics.storeCountByConcentration')}
            {statistics.store_count_by_concentration?.length ? (
              <> — Total: {statistics.store_count_by_concentration.reduce((sum, item) => sum + (parseInt(item.store_count) || 0), 0)}</>
            ) : null}
          </h6>
          {(() => {
            // Debug: ver qué datos llegan
                        
            // Calcular el total de tiendas
            const totalStores = statistics.store_count_by_concentration.reduce((sum, item) => {
                            return sum + (parseInt(item.store_count) || 0);
            }, 0);
            
                        
            return statistics.store_count_by_concentration
              .slice()
              .sort((a, b) => {
                const order = ['Very High', 'High', 'Med', 'Low'];
                return order.indexOf(a.concentration) - order.indexOf(b.concentration);
              })
              .map((item, idx) => {
                // Calcular el porcentaje
                const storeCount = parseInt(item.store_count) || 0;
                const percentage = totalStores > 0 ? ((storeCount / totalStores) * 100).toFixed(1) : 0;
                
                // Calcular el ancho de la barra
                const barWidth = Math.min(storeCount / 200 * 100, 100);
                
                // Determinar el texto a mostrar basado en el ancho de la barra
                const displayText = barWidth < 20 ? `${storeCount}` : `${storeCount} (${percentage}%)`;
                
                // Mapear 'Med' a 'Medium' para el filtro
                let filterValue = item.concentration === 'Med' ? 'Medium' : item.concentration;
                return (
                  <div 
                    className={`bar-group${hispanicConcentration === filterValue ? ' selected' : ''}`}
                    key={item.concentration + '-' + idx}
                    style={{ cursor: 'pointer' }}
                    onClick={() => setHispanicConcentration(hispanicConcentration === filterValue ? '' : filterValue)}
                    title={`${item.concentration} Concentration: ${storeCount} stores (${percentage}% of total stores)`}
                  >
                    <div className="bar-label" style={{marginBottom: '2px'}}>{item.concentration}</div>
                    <div className="bar-bar-container">
                      <div
                        className="bar-bar"
                        style={{
                          width: `${barWidth}%`,
                          background: hispanicConcentration === filterValue ? '#b13a3a' : 
                            item.concentration === 'Very High' ? '#28a745' :
                            item.concentration === 'High' ? '#5cb85c' :
                            item.concentration === 'Med' ? '#7cb342' :
                            '#6c757d',
                          border: hispanicConcentration === filterValue ? '2px solid #b13a3a' : 'none',
                          fontSize: '12px',
                          fontWeight: 'bold',
                          color: '#ffffff',
                          textShadow: '1px 1px 2px rgba(0,0,0,0.7)'
                        }}
                      >
                        {displayText}
                      </div>
                    </div>
                  </div>
                );
              });
          })()}
        </div>
      )}

      {/* Barras: Store Count by DMA Size Rank (NUEVO AVANZADO) */}
      {statistics.store_count_by_dma_size && (
        <div className="bar-section" style={{display: 'none'}}>
          <h6>{t('statistics.storeCountByDmaSize')}</h6>
          {statistics.store_count_by_dma_size.map((item, idx) => (
            <div 
              className="bar-group" 
              key={item.dma_size + '-' + idx}
              title={`${item.dma_size} DMA Rank: ${item.store_count} stores in this DMA size category`}
            >
              <div className="bar-label" style={{marginBottom: '2px'}}>{item.dma_size}</div>
              <div className="bar-bar-container">
                <div
                  className="bar-bar"
                  style={{
                    width: `${Math.min(item.store_count / 200 * 100, 100)}%`,
                    background: '#444'
                  }}
                >
                  {item.store_count}
                </div>
              </div>
            </div>
          ))}
        </div>
      )}

      {/* Barras: Hispanic Population in Trade Area (NUEVO AVANZADO) */}
      {statistics.hispanic_population_trade_area && (
        <div className="bar-section">
          <h6>{t('statistics.hispanicPopulationTradeArea')}</h6>
          {statistics.hispanic_population_trade_area.map((item, idx) => (
            <div 
              className="bar-group" 
              key={item.dma_name + '-' + idx}
              title={`${item.dma_name}: ${formatNumber(item.hispanic_population)} Hispanic population in trade area`}
            >
              <div className="bar-label" style={{marginBottom: '2px'}}>{item.dma_name}</div>
              <div className="bar-bar-container">
                <div
                  className="bar-bar"
                  style={{
                    width: `${Math.min(item.hispanic_population / (statistics.hispanic_population_trade_area[0]?.hispanic_population || 1) * 100, 100)}%`,
                    background: '#888',
                    overflow: 'hidden',
                    whiteSpace: 'nowrap',
                    textOverflow: 'ellipsis',
                    fontSize: '13px',
                    paddingRight: '6px'
                  }}
                >
                  {abbreviateNumber(item.hispanic_population)}
                </div>
              </div>
            </div>
          ))}
        </div>
      )}

      {/* Barras: Coverage: % DMA Hisp Population in Trade Area (NUEVO AVANZADO) */}
      {statistics.coverage_dma_hisp && (
        <div className="bar-section">
          <h6>{t('statistics.coverageDmaHispanic')}</h6>
          {statistics.coverage_dma_hisp.map((item, idx) => (
            <div 
              className="bar-group" 
              key={item.dma_name + '-' + idx}
              title={`${item.dma_name}: ${item.hispanic_coverage}% Hispanic population coverage in DMA`}
            >
              <div className="bar-label" style={{marginBottom: '2px'}}>{item.dma_name}</div>
              <div className="bar-bar-container">
                <div
                  className="bar-bar"
                  style={{
                    width: `${Math.min(item.hispanic_coverage, 100)}%`,
                    background: '#6b2323'
                  }}
                >
                  {item.hispanic_coverage}%
                </div>
              </div>
            </div>
          ))}
        </div>
      )}

      {/* Gráfico */}
      <div className="chart-section">
        <h6><i className="fas fa-chart-donut"></i> {t('statistics.distribution')}</h6>
        <div className="chart-container">
          <canvas ref={chartRef}></canvas>
        </div>
      </div>
     
    </div>
  );
};

export default Sidebar; 